import { Box, Divider } from '@mui/material';
import { TournamentStatus } from '@okletsplay/core';
import { ModalName } from 'app/enums';
import hatIcon from 'assets/images/hat_icon.svg';
import { TournamentDescription } from 'modules/tournaments/components/TournamentDescription';
import { CanceledTournament } from 'modules/tournaments/components/TournamentModal/CanceledTournament';
import { TournamentActions } from 'modules/tournaments/components/TournamentModal/TournamentActions';
import { TournamentSettings } from 'modules/tournaments/components/TournamentDetails';
import { TournamentModalActions } from 'modules/tournaments/components/TournamentModal/TournamentModalActions';
import { TournamentModalHeader } from 'modules/tournaments/components/TournamentModal/TournamentModalHeader';
import { TournamentPrizes } from 'modules/tournaments/components/TournamentModal/TournamentPrizes';
import { TournamentSharedLink } from 'modules/tournaments/components/TournamentModal/TournamentSharedLink';
import { TournamentPendingExpirationNote } from 'modules/tournaments/components/TournamentModal/TournamentPendingExpirationNote';
import {
  useTournamentDetails,
  useTournamentModal,
  useTournamentSettings,
  useTournamentCurrentPrizes,
  useSharedLink,
} from 'modules/tournaments/hooks';
import { TournamentSettingsOption } from 'modules/tournaments/interfaces';
import React, { FC, useMemo } from 'react';
import { EntriesFormat } from 'shared/components/Format/EntriesFormat';
import { Modal } from 'shared/components/Modal';
import { withModalOpenChecker } from 'shared/HOC/withModalOpenChecker';
import { AnnouncementButton } from 'shared/components/AnnouncementButton';
import { ShareButton } from 'shared/components/ShareButton';
import { EditTournamentButton } from '../EditTournamentButton';
import { MyCurrentRankings } from './MyCurrentRankings';
import { useStyles } from './TournamentModal.styles';
import { TournamentSettingsTitle } from '../TournamentSettingsTitle';
import { useSelector } from 'react-redux';
import { currentTournamentSelector } from 'modules/tournaments/store/current-tournament/selectors';
import { GameCurrency, GameCurrencyWithFee } from 'modules/game-payments/components';
import { ToggleTournamentVisibilityButton } from 'shared/components/ToggleTournamentVisibilityButton';
import { TournamentSponsoredIndicator } from 'shared/components/TournamentSponsoredIndicator';

const TournamentModalComponent: FC = () => {
  const { classes } = useStyles();
  const tournament = useSelector(currentTournamentSelector.tournament);
  const isCurrentUserAdmin = useSelector(currentTournamentSelector.isCurrentUserAdmin);
  const isSponsored = useSelector(currentTournamentSelector.isSponsoredTournament);
  const isSinglePlayer = useSelector(currentTournamentSelector.isSinglePlayer);
  const { closeTournamentModal } = useTournamentModal();
  const { isCanceledTournament, isFinishedTournament } = useTournamentDetails();

  const link = useSharedLink();

  const isPrizesVisible = [TournamentStatus.Pending, TournamentStatus.Live].includes(
    tournament.status
  );

  const { teamsSize } = useTournamentSettings(tournament);

  const { currentTotalPrize, currentDistributionBreakdown } =
    useTournamentCurrentPrizes(tournament);

  const tournamentSettings = useMemo((): TournamentSettingsOption[] => {
    return [
      {
        label: 'prizes',
        value: <GameCurrency amount={currentTotalPrize} />,
        needsTooltip: true,
      },
      { label: isSinglePlayer ? 'players' : 'teams', value: teamsSize },
      {
        label: 'entry fee',
        value: (
          <GameCurrencyWithFee amount={tournament?.prizeAndDistribution?.playerEntryFee || 0} />
        ),
        needsTooltip: true,
      },
      {
        label: 'plays per entry fee',
        value: <EntriesFormat entries={tournament?.matchSetupSettings?.maxSubmissions} />,
      },
      {
        label: 'max prize positions',
        value: <EntriesFormat entries={tournament?.matchSetupSettings?.maxRankedSubmissions} />,
      },
    ];
  }, [
    currentTotalPrize,
    teamsSize,
    tournament?.prizeAndDistribution?.playerEntryFee,
    tournament?.matchSetupSettings?.maxSubmissions,
    tournament?.matchSetupSettings?.maxRankedSubmissions,
    isSinglePlayer,
  ]);

  if (!tournament) {
    return null;
  }

  return (
    <Modal
      onClose={closeTournamentModal}
      maxWidth="md"
      contentHeight={630}
      name={ModalName.Tournament}
      disableEnforceFocus={true}
      actions={<TournamentModalActions tournament={tournament} status={tournament.status} />}
      header={<TournamentModalHeader tournament={tournament} />}
    >
      <>
        <Box className={classes.subheader}>
          {isSponsored && <TournamentSponsoredIndicator withSmallIcon />}
          <Box className={classes.iconsContainer}>
            <EditTournamentButton />
            <ToggleTournamentVisibilityButton />
            <AnnouncementButton />
            <ShareButton link={link} />
          </Box>
        </Box>
        <Divider />
        {isCanceledTournament ? (
          <CanceledTournament />
        ) : (
          <Box className={classes.root}>
            <MyCurrentRankings tournament={tournament} />
            {isPrizesVisible && (
              <TournamentPrizes distributionBreakdown={currentDistributionBreakdown} />
            )}
            {tournament.description && (
              <Box pb={2}>
                <TournamentDescription content={tournament.description} />
              </Box>
            )}
            <TournamentSettings
              tournamentSettings={tournamentSettings}
              title="Tournament Details"
            />
            {tournament?.status === TournamentStatus.Pending ? (
              <Box className={classes.timerNote}>
                <TournamentPendingExpirationNote
                  startDate={tournament.createdAt}
                  maxDuration={tournament.matchSetupSettings.maxPendingDuration}
                />
              </Box>
            ) : null}
            <Box className={classes.shareLink}>
              <TournamentSharedLink tournamentId={tournament?.id} />
            </Box>
            {isCurrentUserAdmin && !isFinishedTournament && (
              <Box>
                <Box className={classes.adminTitleContainer}>
                  <TournamentSettingsTitle>Admin</TournamentSettingsTitle>
                  <img className={classes.hatIcon} src={hatIcon} alt="Hat icon" />
                </Box>
                <TournamentActions tournament={tournament} />
              </Box>
            )}
          </Box>
        )}
      </>
    </Modal>
  );
};

export const TournamentModal = withModalOpenChecker(TournamentModalComponent, ModalName.Tournament);
